import React from 'react';
import { Lock, FileText, Leaf, Building2, Zap, Droplets, Cable, ClipboardList, MessageSquare } from 'lucide-react';
import type { ReportTypeDefinition } from '../../config/reportTypes';
import { reportTypeTagline } from '../../lib/reportTypeTagline';
import {
  REPORT_CARD_DESCRIPTION_CLASS,
  REPORT_CARD_TITLE_CLASS,
} from './reportCardTypography';

const ICON_MAP: Record<string, React.ElementType> = {
  FileText, Leaf, Building2, Zap, Droplets, Cable, ClipboardList, MessageSquare,
};

export interface ReportTypeLaunchCardProps {
  type: ReportTypeDefinition;
  onClick?: () => void;
  disabled?: boolean;
  existingVersion?: number;
  testId?: string;
  actions?: React.ReactNode;
}

/**
 * Shared report-type launch card — used on the Reports tab empty state
 * and in NewReportWizard step 1 so both surfaces share one visual idiom.
 */
export function ReportTypeLaunchCard({
  type,
  onClick,
  disabled,
  existingVersion,
  testId,
  actions,
}: ReportTypeLaunchCardProps) {
  const Icon = ICON_MAP[type.icon] ?? FileText;
  const selectable = !disabled && (type.status === 'active' || type.status === 'beta');
  const tagline = reportTypeTagline(type);
  const tileTestId = testId ?? `report-type-tile-${type.id}`;

  const ariaLabel = (() => {
    const v = existingVersion !== undefined
      ? `, existing version ${existingVersion}, next would be v${existingVersion + 1}`
      : '';
    if (!selectable) return type.status === 'retired' ? `${type.label} — retired` : `${type.label} — coming ${type.expectedDate ?? 'soon'}`;
    return `${type.label}${v}`;
  })();

  return (
    <div
      data-testid={tileTestId}
      className={[
        'relative w-full p-4 border border-border rounded-lg text-left transition-colors flex flex-col gap-3 min-h-0',
        selectable
          ? 'hover:border-primary hover:bg-state-hover'
          : 'opacity-50',
      ].join(' ')}
    >
      <button
        type="button"
        disabled={!selectable}
        onClick={() => { if (selectable) onClick?.(); }}
        aria-label={ariaLabel}
        className="absolute inset-0 z-0 w-full h-full cursor-pointer disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 rounded-lg"
      />

      <div className="flex flex-col gap-1 w-full pointer-events-none z-10">
        <div className="flex items-start justify-between gap-3 w-full">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <Icon className="w-6 h-6 text-primary shrink-0 mt-0.5" aria-hidden />
            <div
              className={`${REPORT_CARD_TITLE_CLASS} pt-0.5`}
              title={type.shortLabel}
              data-testid={`report-type-card-label-${type.id}`}
            >
              {type.shortLabel}
            </div>
          </div>
          <div className="shrink-0 pointer-events-auto flex items-center gap-2 text-right">
            {actions}
            {selectable && existingVersion !== undefined && (
              <span
                data-testid={`report-type-tile-version-${type.id}`}
                data-has-version="true"
                className="type-body font-bold uppercase tracking-widest px-1.5 py-0.5 bg-state-hover text-text-secondary border border-border-subtle whitespace-nowrap pointer-events-none"
              >
                v{existingVersion}
              </span>
            )}
            {!selectable && type.status === 'coming_soon' && type.expectedDate && (
              <span className="type-body font-bold uppercase tracking-widest px-1.5 py-0.5 bg-state-hover text-text-muted border border-border-subtle whitespace-nowrap pointer-events-none">
                {type.expectedDate}
              </span>
            )}
            {!selectable && type.status === 'coming_soon' && !type.expectedDate && (
              <span
                data-testid={`report-type-card-lock-${type.id}`}
                title="Coming soon — this report type is still in development. Contact your tenant admin if you need early access."
                aria-label="Coming soon — contact your tenant admin for early access."
                className="inline-flex items-center justify-center cursor-help pointer-events-auto"
              >
                <Lock aria-hidden className="w-3.5 h-3.5 text-text-disabled" />
              </span>
            )}
          </div>
        </div>
        
        <div className={`${REPORT_CARD_DESCRIPTION_CLASS} pl-9 pt-1`}>
          {tagline}
        </div>
      </div>
    </div>
  );
}
